
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardBody,
  CardHead,
  ConfirmDialog,
  Field,
  Input,
  Toggle,
} from '../../components/ui';
import { useEnrichLibrary } from '../../hooks';
import type { Id } from '../../types/api';
import { isTaskFinished } from '../../types/api';
import styles from './LibraryPage.module.css';

interface Props {
  canEdit: boolean;
  onClose?: () => void;
  itemIds?: Id[];
  untagged?: number;
}

const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  enriched: 'размечено',
  planned: 'модель разметила бы',
  skipped: 'пропущено, уже размечено',
  failed: 'ошибка',
};

interface OutcomeRow {
  itemId: string;
  title: string;
  outcome: string;
  reason: string | null;
}

export function EnrichPanel({ canEdit, onClose, itemIds, untagged = 0 }: Props) {
  const single = Boolean(itemIds?.length);
  const [limit, setLimit] = useState('20');
  const [frames, setFrames] = useState('3');
  const [force, setForce] = useState(single);
  const [confirming, setConfirming] = useState(false);
  const [lastMode, setLastMode] = useState<'dry' | 'apply' | null>(null);

  const { enrich, task } = useEnrichLibrary();
  const running = enrich.isPending || task.isRunning;
  const finished = isTaskFinished(task.task.data?.status);
  const result = task.task.data?.result;
  const wasDryRun = result ? Boolean(result.dry_run) : null;

  const rows = useMemo<OutcomeRow[]>(() => {
    const raw = result?.items;
    if (!Array.isArray(raw)) return [];
    return raw.map((entry) => {
      const item = entry as Record<string, unknown>;
      return {
        itemId: String(item.item_id ?? ''),
        title: String(item.title ?? ''),
        outcome: String(item.outcome ?? ''),
        reason: item.reason == null ? null : String(item.reason),
      };
    });
  }, [result]);

  const parsedLimit = Number.parseInt(limit, 10);
  const parsedFrames = Number.parseInt(frames, 10);
  const problems: string[] = [];
  if (!single && (!Number.isFinite(parsedLimit) || parsedLimit < 1 || parsedLimit > 1000)) {
    problems.push('Сколько записей взять: от 1 до 1000');
  }
  if (!Number.isFinite(parsedFrames) || parsedFrames < 1 || parsedFrames > 8) {
    problems.push('Кадров на запись: от 1 до 8');
  }

  function run(dryRun: boolean) {
    if (!canEdit || running || problems.length > 0) return;
    setConfirming(false);
    setLastMode(dryRun ? 'dry' : 'apply');
    enrich.mutate({
      ...(single ? { item_ids: itemIds } : { limit: parsedLimit }),
      dry_run: dryRun,
      force,
      frames: parsedFrames,
    });
  }

  const target = single
    ? `${itemIds?.length ?? 0} ${itemIds?.length === 1 ? 'записи' : 'записей'}`
    : `до ${parsedLimit || 0} записей`;

  return (
    <Card className={single ? styles.subPanel : undefined}>
      <CardHead
        title="Авто-разметка"
        hint={
          single
            ? undefined
            : untagged > 0
              ? `в очереди ${untagged} неразмеченных`
              : 'неразмеченных нет'
        }
        actions={
          onClose ? (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={onClose}
              aria-label="Закрыть панель разметки"
            >
              <X size={13} strokeWidth={1.8} />
            </Button>
          ) : undefined
        }
      />

      <CardBody pad className={styles.panelBody}>
        {!canEdit && (
          <div className={styles.notice}>Роль «наблюдатель» не запускает разметку.</div>
        )}

        <div className={styles.enrichRow}>
          {!single && (
            <Field layout="stacked" label="Сколько записей взять" className={styles.inlineField}>
              {(id) => (
                <Input
                  id={id}
                  size="sm"
                  mono
                  type="number"
                  min={1}
                  max={1000}
                  value={limit}
                  onChange={(event) => setLimit(event.target.value)}
                  disabled={running}
                />
              )}
            </Field>
          )}
          <Field layout="stacked" label="Кадров на запись" className={styles.inlineField}>
            {(id) => (
              <Input
                id={id}
                size="sm"
                mono
                type="number"
                min={1}
                max={8}
                value={frames}
                onChange={(event) => setFrames(event.target.value)}
                disabled={running}
              />
            )}
          </Field>
          <Toggle
            checked={force}
            onChange={setForce}
            disabled={running}
            label="перечитать уже размеченные"
          />
        </div>

        {problems.length > 0 && (
          <div className={styles.problems} role="alert">
            {problems.map((problem) => (
              <div key={problem}>{problem}</div>
            ))}
          </div>
        )}

        {enrich.error && <ErrorBanner error={enrich.error} />}
        {task.task.error && !task.task.error.isNotFound && <ErrorBanner error={task.task.error} />}
        {task.task.error?.isNotFound && (
          <div className={styles.notice}>
            Статус задачи потерян. Разметка пишется по мере работы: обновите библиотеку.
          </div>
        )}

        {running && (
          <div className={styles.notice}>
            Модель смотрит кадры, это минуты. Экран можно закрыть, задача не прервётся.
          </div>
        )}

        {finished && result && (
          <div className={wasDryRun ? styles.dryResult : styles.applyResult}>
            <div className={styles.resultHead}>
              {wasDryRun ? 'Предпросмотр, ничего не записано' : 'Разметка записана'}
              <span className={styles.resultCounts}>
                обработано {String(result.found ?? 0)} · ошибок {String(result.failed ?? 0)}
              </span>
            </div>
            {rows.length > 0 && (
              <ul className={styles.resultList}>
                {rows.slice(0, 50).map((row) => (
                  <li key={row.itemId || row.title}>
                    <span className={styles.resultTitle} title={row.title}>
                      {row.title}
                    </span>
                    <span
                      className={
                        row.outcome === 'failed' ? styles.stage_err : styles.stage_idle
                      }
                    >
                      {OUTCOME_LABELS[row.outcome] ?? row.outcome}
                      {row.reason ? `: ${row.reason}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {rows.length > 50 && (
              <div className={styles.panelHint}>
                показаны первые 50 из {rows.length}
              </div>
            )}
            {wasDryRun && (
              <div className={styles.panelHint}>
                Сами теги видно только после того, как разметка применена.
              </div>
            )}
          </div>
        )}

        <div className={styles.panelActions}>
          <Button
            onClick={() => run(true)}
            disabled={!canEdit || running || problems.length > 0}
            title="Модель отработает, в библиотеку ничего не запишется"
          >
            {running && lastMode === 'dry' ? 'Считаем…' : 'Посмотреть, что предложит'}
          </Button>

          <Button
            variant="primary"
            onClick={() => setConfirming(true)}
            disabled={!canEdit || running || problems.length > 0}
          >
            {running && lastMode === 'apply' ? 'Размечаем…' : 'Разметить'}
          </Button>
        </div>
      </CardBody>

      <ConfirmDialog
        open={confirming}
        title="Разметить?"
        body={`Модель перезапишет теги, настроение и гео у ${target}. Старые значения не сохранятся.`}
        confirmLabel="Да, разметить"
        destructive
        busy={running}
        onConfirm={() => run(false)}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}
